import React, { useEffect, useRef } from 'react';
import {
  FileUpload as CustomFileUpload,
  FileUploadSelectEvent,
} from 'primereact/fileupload';
import { FileUploadProps } from './FileUploadProps';
import { useFileUploadStore } from '@/store/FileUploadStore/FileUploadStore';

const FileUpload: React.FC<FileUploadProps> = ({
  id,
  placeholder,
  labelStyle,
  inputStyle,
  label,
  handleChange,
  errorStyle,
  helpText,
  name,
  setValue,
  allowedFileTypes = [],
  initialSelectedFile,
  disabled,
  fileUploadStyle,
  icon,
}) => {
  const fileUploadRef = useRef<CustomFileUpload>(null);
  const {
    selectedFileName,
    uploadedFile,
    setSelectedFileName,
    setUploadedFile,
    resetFileUpload,
  } = useFileUploadStore();

  useEffect(() => {
    if (initialSelectedFile) {
      setUploadedFile(initialSelectedFile);
    }
  }, [initialSelectedFile, setUploadedFile]);

  const handleFileSelect = (e: FileUploadSelectEvent) => {
    const file = e.files[0];

    if (file) {
      if (name) setValue && setValue(name, file, { shouldValidate: true });
      setSelectedFileName(file.name);
      setUploadedFile('');
      if (handleChange) {
        handleChange(file);
      }
      if (fileUploadRef.current) {
        fileUploadRef.current.clear();
      }
    }
    if (!file) {
      setSelectedFileName('');
      if (name) setValue && setValue(name, '', { shouldValidate: true });
    }
  };

  return (
    <div className="flex flex-col overflow-hidden max-h-12">
      <div
        className={`${icon ? `flex items-center text-iconColor bg-[#1D1D1D] px-3 rounded-[4.2px] ${fileUploadStyle}` : ''}`}
      >
        {icon}
        <div className="flex flex-col flex-grow">
          <div
            className={`flex items-center justify-between bg-[#1D1D1D] text-neutral-400 px-4 rounded-[4.2px] ${fileUploadStyle}`}
          >
            <div className="flex items-center space-x-2">
              {!selectedFileName && uploadedFile && (
                <div className="w-12 overflow-hidden bg-white">
                  {uploadedFile.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                    <img
                      src={initialSelectedFile}
                      alt="Image sélectionnée"
                      className="w-full h-auto object-cover"
                    />
                  ) : uploadedFile.match(/\.(mp3|wav|ogg)$/i) ? (
                    <audio controls className="h-9">
                      <source src={initialSelectedFile} type="audio/mpeg" />
                      Votre navigateur ne supporte pas la lecture audio.
                    </audio>
                  ) : null}
                </div>
              )}

              {selectedFileName && !uploadedFile && (
                <>
                  {selectedFileName.startsWith('http') &&
                  selectedFileName.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                    <div className="w-24 overflow-hidden bg-white">
                      <img
                        src={selectedFileName}
                        alt="Miniature"
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  ) : (
                    <span
                      className={`text-sm text-white font-raleway w-[8rem] overflow-hidden ${inputStyle}`}
                    >
                      {selectedFileName}
                    </span>
                  )}
                </>
              )}
              {!selectedFileName && !uploadedFile && placeholder && (
                <span
                  className={`text-sm text-iconColor font-raleway ${inputStyle}`}
                >
                  {placeholder}
                </span>
              )}
            </div>

            <div className="relative">
              <CustomFileUpload
                ref={fileUploadRef}
                id={id}
                mode="basic"
                accept={allowedFileTypes.join(',')}
                onSelect={handleFileSelect}
                className="min-h-12 font-raleway text-sm"
                chooseOptions={{
                  className:
                    'bg-transparent text-[#FFD964] underline text-[10px] pt-4 focus:none font-raleway',
                  label: 'Upload',
                  icon: 'hidden',
                }}
                disabled={disabled}
              />
            </div>
          </div>
          <label
            htmlFor={id}
            className={`block text-xs text-[#616161] font-medium ${labelStyle}`}
          >
            {label}
          </label>
        </div>
      </div>
      <div className={`font-raleway text-xs absolute top-3/4 ${errorStyle}`}>
        {helpText}
      </div>
    </div>
  );
};

export default FileUpload;
